import React from 'react';
import { Container, Typography, Box, Card, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Code, School } from '@mui/icons-material';

const Timeline = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '1100px',
  margin: '0 auto',
  paddingLeft: '56px',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '32px',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '14px',
    bottom: '14px',
    left: '15px',
    width: '2px',
    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '2px',
    [theme.breakpoints.down('sm')]: {
      left: '7px',
    },
  },
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: '32px',
  '&:last-child': {
    marginBottom: 0,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-49px',
    top: '28px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: '3px solid #ffffff',
    boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.18)',
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      left: '-29px',
    },
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  border: '1px solid rgba(102, 126, 234, 0.1)',
  borderRadius: '20px',
  padding: '24px',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  flexShrink: 0,
  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
}));

const PeriodChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
  boxShadow: '0 4px 15px rgba(240, 147, 251, 0.3)',
}));

const TechChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  margin: '4px 4px 4px 0',
  fontSize: '0.75rem',
  height: '24px',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  transition: 'all 0.3s ease',
}));

const experiences = [
  {
    role: 'Full Stack Engineer',
    type: 'Part-time',
    company: 'Aileen Bay',
    location: 'Remote',
    period: 'Nov 2025 – Present',
    icon: <Code fontSize="large" />,
    description:
      'Building a CRM tool, Order Management System, and an AI-powered Order Placement Assistant to streamline business operations end to end.',
    tech: ['React', 'Node.js', 'TypeScript', 'LLMs'],
  },
  {
    role: 'Coding Instructor',
    type: 'Part-time',
    company: 'Coderpals',
    location: 'Remote',
    period: 'Aug 2024 – Sep 2024',
    icon: <School fontSize="large" />,
    description:
      'Taught programming fundamentals, game development, and creative problem-solving to kids using Python through interactive lessons and project-based learning.',
    tech: ['Python', 'Game Dev', 'Teaching', 'Competitive Programming'],
  },
];

const Experience = () => {
  return (
    <Container
      id="experience"
      maxWidth="xl"
      sx={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
      }}
    >
      <Box textAlign="center" sx={{ marginBottom: 6 }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 700,
            marginBottom: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Work Experience
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: '#666',
            maxWidth: '800px',
            margin: '0 auto',
            lineHeight: 1.8,
          }}
        >
          Bridging research and industry — applying engineering and AI skills to ship
          real products while mentoring the next generation of programmers.
        </Typography>
      </Box>

      <Timeline>
        {experiences.map((exp, index) => (
          <TimelineItem key={index}>
            <StyledCard>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  marginBottom: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconWrapper>{exp.icon}</IconWrapper>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {exp.role}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999', fontWeight: 500 }}>
                        · {exp.type}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                      {exp.company} · {exp.location}
                    </Typography>
                  </Box>
                </Box>
                <PeriodChip label={exp.period} size="small" />
              </Box>

              <Typography sx={{ marginBottom: 3, color: '#555', lineHeight: 1.7 }}>
                {exp.description}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {exp.tech.map((t, i) => (
                  <TechChip key={i} label={t} size="small" />
                ))}
              </Box>
            </StyledCard>
          </TimelineItem>
        ))}
      </Timeline>
    </Container>
  );
};

export default Experience;
